import { Box, Flex, FormControl, FormLabel } from '@chakra-ui/react';
import { FormSwitch } from 'components/UI';
import { validateForm } from 'utils/helpers';
import { FormikErrors, FormikHandlers, FormikHelpers, FormikState } from 'formik';
import InputWhite from 'components/common/InputWhite';
import FieldDescription from 'components/common/FieldDescription';
import FieldError from 'components/common/FieldError/FieldError';
import {
  messageRoleAnonymousDisabled,
  messageRoleAnonymousInfo,
  messageRoleCurrentAnonymous,
  messageRoleCurrentDefault,
  messageRoleDefaultInfo,
} from 'constants/messages';
import { ISettingsValues } from 'types';
import { IRoleResource } from '../../../../../../server/types';

type InfoFormProps = {
  values: Pick<IRoleResource, 'name' | 'isDefault' | 'isAnonymous'>;
  submitted: boolean;
  errors: FormikErrors<IRoleResource | Record<string, never>>;
  data?: Partial<IRoleResource>;
  onBlur: FormikHandlers['handleBlur'];
  onChange: FormikHandlers['handleChange'];
  touched: FormikState<IRoleResource | Record<string, never>>['touched'];
  setFieldValue: FormikHelpers<IRoleResource | Record<string, never>>['setFieldValue'];
  settings: ISettingsValues | Record<string, never>;
};

const InfoForm = ({ values, data, setFieldValue, onChange, onBlur, settings, ...props }: InfoFormProps) => {
  const formErrors = validateForm<IRoleResource | Record<string, never>>(props);

  const renderDefaultText = data?.isDefault ? messageRoleCurrentDefault : messageRoleDefaultInfo;

  const renderAnonymousText = data?.isAnonymous ? messageRoleCurrentAnonymous : messageRoleAnonymousInfo;

  const renderAnonymousError =
    !settings.anonymousAccess || settings.anonymousAccess === 'false' ? (
      <FieldError error={messageRoleAnonymousDisabled} color="red.500" />
    ) : null;

  const renderAnonymousField = values?.isAnonymous ? (
    <FormControl isInvalid={!!formErrors.isAnonymous} mb={4}>
      <Flex>
        <FormLabel htmlFor="isAnonymous">ANONYMOUS</FormLabel>
        <FormSwitch
          name="isAnonymous"
          value={values?.isAnonymous ?? false}
          ml={4}
          isDisabled
          onChange={(e) => setFieldValue('isAnonymous', e.target.checked)}
        />
      </Flex>
      {renderAnonymousError}
      <FieldDescription w="full">{renderAnonymousText}</FieldDescription>
    </FormControl>
  ) : null;

  return (
    <Box>
      <FormControl isInvalid={!!formErrors.name} mb={4}>
        <FormLabel htmlFor="name">NAME *</FormLabel>
        <InputWhite
          type="name"
          name="name"
          maxLength={100}
          value={values.name}
          error={formErrors.name}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Enter the name"
          autoComplete="off"
        />
      </FormControl>

      {renderAnonymousField}
      <FormControl isInvalid={!!formErrors.isDefault}>
        <Flex>
          <FormLabel htmlFor="isDefault">DEFAULT</FormLabel>
          <FormSwitch
            name="isDefault"
            value={values?.isDefault ?? false}
            ml={4}
            isDisabled={data?.isDefault}
            onChange={(e) => setFieldValue('isDefault', e.target.checked)}
          />
        </Flex>
        <FieldDescription w="full">{renderDefaultText}</FieldDescription>
      </FormControl>
    </Box>
  );
};

export default InfoForm;
